import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { getLinksForTranslation } from '../components/atoms/Link';
import LoadingIndicator from '../components/atoms/LoadingIndicator';
import { showNotification } from '../components/molecules/Notifications';
import EmptyBasket from '../components/organisms/EmptyBasket';
import PaymentConfirmationPending from '../components/organisms/PaymentConfirmationPending';
import { Package } from '../types/basket';
import { config } from '../utils/config';
import callApi from './callApi';
import { Country } from './countryData';
import getMutations from './getMutations';
import { reportErrorToSentry } from './initializeSentry';
import redirect from './redirect';
import { pushTrackingEvent } from './tracking';
import useBasketNotifications from './useBasketNotifications';
import useIsMobileDevice from './useIsMobileDevice';
import usePolling from './usePolling';
import usePrevious from './usePrevious';

export type Customer = {
  authentication: { data: any; type: string };
  data: {
    birthDate?: string;
    customData?: any;
    email: string;
    firstName: string;
    gender: string;
    id: number;
    lastName: string;
    trackingHash?: string;
    publicKey: string;
    referenceKey?: string;
    title?: string;
    type: string;
    phone?: { plainText: string };
  };
  flags?: { canUpgradeToFullAccount?: boolean; isGuest: boolean };
  groups: string[];
};

export type Campaign = { isApplicable: boolean; name: string };
export type Newsletter = { customerIsConfirmedSubscriber: boolean; enabled: boolean };

export type PaymentOption = {
  paymentOptionKey: string;
  paymentOptionProvider: string;
  configuration?: {
    initializationData?: any;
    deferral?: { autoApplyDeferral: boolean; isSelected: boolean; isSupported: boolean; weeks: number };
    fee?: number;
    instalments?: any[];
    public?: { requiresTranslation?: boolean };
  };
  isDisabled: boolean;
  notifications?: Notification[];
  serviceCosts?: { total: number };
  url?: string;
};

export type RecurringPaymentOption = PaymentOption & {
  recurringPaymentOptionId: string;
  recurringPaymentInformation?: {
    maskedCreditcardNumber?: string;
  };
};

export type GiftCard = RecurringPaymentOption & {
  recurringPaymentInformation: {
    giftCardCode: string;
    remainingAmount: number;
  };
};

export type DeliveryOption = {
  carrierGroupKey: string;
  carrierKey: string;
  deliveryOptionKey: string;
  shippingOptionKey: string;
  collectionPointType?: string;
  isDisabled: boolean;
  serviceCosts?: { total: number };
  deliveryEstimations?: { deliveryDateTime: { iso8601: string }; deliveryDays: { maximum: number; minimum: number } };
  requiresDateSchedule?: boolean;
  requiresTimeSchedule?: boolean;
};

export type PaymentOptions = {
  generic: PaymentOption[];
  giftCards?: GiftCard[];
  recurring?: RecurringPaymentOption[];
};

export type ShippingOptions = {
  collectionPointDelivery?: DeliveryOption[];
  homeDelivery?: DeliveryOption[];
  onlineDelivery?: DeliveryOption[];
};

export type OrderOptions = { payment?: PaymentOptions; shipping?: ShippingOptions };

export type SimpleAddress = {
  street: string;
  houseNumber: string;
  zipCode?: string;
  city: string;
};

export type BaseAddress = SimpleAddress & {
  country: { iso2Code: Country };
  title?: string;
  salutationCode?: 'f' | 'm';
  firstName: string;
  lastName: string;
  additional?: string;
  state?: string;
  phone?: string;
};

export type Address = BaseAddress & {
  id: number;
  hash: string;
};

export type CollectionPointAddressPart = {
  collectionPoint?: { customerId?: any; description: string; id: string; type: string };
};

export type ShippingAddress = Address & CollectionPointAddressPart;

export type AddressSuggestion = BaseAddress & {
  isOriginal: boolean;
  signature: string;
};

export type Voucher = {
  discount: number;
  id: string;
  isAppliedAutomatically: boolean;
  name: string;
  type: string;
  value: number;
  code: string;
};

export type Basket = {
  packages: Package[];
  tax: any;
  total: {
    costPaymentServiceCosts: number;
    costShippingServiceCosts: number;
    costWithTaxWithVouchersWithServiceCosts: number;
    costWithTaxWithVouchersWithServiceCostsWithGiftCard: number;
    costWithTaxWithVouchersWithServiceCostsWithoutGiftCard: number;
    costWithTaxWithVouchersWithoutServiceCosts: number;
    costWithTaxWithoutAbsoluteVouchersWithoutServiceCosts: number;
  };
  serviceCosts?: any[];
  discount: any;
  vouchers?: Voucher[];
};

export type OrderState = {
  id?: number;
  addresses?: { billing?: Address; shipping?: ShippingAddress };
  basket: Basket;
  memberships?: { activated?: ActiveMembership[]; suggestions: Membership[] };
  payment?: { giftCards?: GiftCard[]; selected?: (PaymentOption & Partial<RecurringPaymentOption>)[] };
  // TODO Waiting for BE fix: should be logisticsContact?: { phone: { plainText: string } };
  shipping?: { logisticsContact?: { plainText: string }; selected?: DeliveryOption[] };
  vouchers?: Voucher[];
  taxId?: string;
};

export type Membership = { membershipTypeKey: string; earnings: { points: number } };
export type ActiveMembership = Membership & { membershipAccountNumber: string };

export type Notification = {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  textTranslationKey?: string;
  text?: string;
  titleTranslationKey?: string;
};

export type Redirect = { method: 'GET' | 'POST'; url: string; parameters?: { name: string; value: string }[] };

export type CheckoutState = {
  addressSuggestions?: { suggestions: AddressSuggestion[] };
  customer?: Customer;
  integrations: { campaigns?: Campaign[]; newsletter: Newsletter };
  orderOptions: OrderOptions;
  orderState: OrderState;
  notifications?: Notification[];
  orderConfirmation?: { redirect?: Redirect; initializationData?: any };
};

type Mutations = ReturnType<typeof getMutations>;

type CheckoutStateContext = {
  checkoutState: CheckoutState;
  setBasketEmpty: (isBasketEmpty: boolean) => void;
  setPollingEnabled: (enabled: boolean) => void;
} & Mutations;

let basketId: string | undefined;

export let setBasketId = (newBasketId: string): void | Promise<void> => {
  basketId = newBasketId;
};

const CheckoutStateContext = createContext<CheckoutStateContext>({} as any);

type CheckoutStateProviderProps = {
  children: ReactNode;
  basketId?: string;
  voucher?: string;
  campaignKey?: string;
  onUpdate?: (checkoutState: CheckoutState) => void;
};

export const CheckoutStateProvider = (props: CheckoutStateProviderProps) => {
  const { children, basketId: initialBasketId, voucher, campaignKey, onUpdate } = props;

  const [isBasketEmpty, setBasketEmpty] = useState(false);
  const previousIsBasketEmpty = usePrevious(isBasketEmpty);

  const [checkoutState, _setCheckoutState] = useState<CheckoutState>();
  const previousCheckoutState = usePrevious(checkoutState);
  const { triggerBasketUpdateNotification, triggerBasketEmptyNotification } = useBasketNotifications();
  const setCheckoutState = useCallback((newCheckoutState: CheckoutState) => {
    _setCheckoutState(newCheckoutState);
    onUpdate?.(newCheckoutState);
  }, []);
  const mutations = getMutations(setCheckoutState);

  const isMobile = useIsMobileDevice();
  const device = config.origin === 'app' ? 'app' : isMobile ? 'mobile' : 'desktop';

  const isWaitingForPaymentConfirmation = new URLSearchParams(window.location.search).get('waitForPaymentConfirmation');

  const fetchCheckoutState = useCallback(async (isPolling?: boolean) => {
    try {
      let newState;
      if ((basketId || voucher || campaignKey) && !isPolling) {
        newState = await callApi<CheckoutState>({
          endpoint: '/state',
          method: 'PUT',
          payload: { device, basketId, voucher, campaignKey },
          failedStatusCodes: [204],
          sequential: true,
        });
      } else {
        newState = await callApi<CheckoutState>({
          endpoint: '/state',
          failedStatusCodes: [204],
          sequential: true,
        });
      }

      if (isPolling) {
        triggerBasketUpdateNotification(newState.orderState.basket, previousCheckoutState.current?.orderState.basket);
      }

      setCheckoutState(newState);
    } catch (e: any) {
      if ([204, 404, 410].includes(e.statusCode)) {
        setBasketEmpty(true);
        setPollingEnabled(false);
        if (isPolling && !previousIsBasketEmpty.current) {
          triggerBasketEmptyNotification();
        }
        return;
      }
      pushTrackingEvent({
        event: 'error',
        action: 'fetch_checkout_state',
        label: `${config.country}|${e.statusCode}|${e.message}`,
        value: null,
      });
      console.error(e);
      reportErrorToSentry(e);
    }
  }, []);
  const poll = useCallback(() => fetchCheckoutState(true), [fetchCheckoutState]);
  const { initializePolling, setPollingEnabled } = usePolling(poll, config.statePollInterval);
  useEffect(() => {
    (async () => {
      basketId = initialBasketId;
      if (!isWaitingForPaymentConfirmation) {
        await fetchCheckoutState();
        initializePolling();
      }
    })();
  }, []);
  setBasketId = async (newBasketId: string) => {
    basketId = newBasketId;
    setPollingEnabled(false);
    await fetchCheckoutState();
    setPollingEnabled(true);
    initializePolling();
  };

  const { formatMessage } = useIntl();

  if (isBasketEmpty || (checkoutState?.orderState && !checkoutState.orderState.basket?.packages?.length)) {
    return <EmptyBasket />;
  }

  // TODO Can this be moved to a route then?
  if (isWaitingForPaymentConfirmation) {
    return <PaymentConfirmationPending />;
  }

  if (!checkoutState) {
    return <LoadingIndicator />;
  }

  if (
    checkoutState.orderConfirmation?.redirect &&
    !(
      checkoutState.orderState &&
      (
        [
          'computop_creditcard',
          'computop_creditcard_mastercard',
          'computop_creditcard_visa',
          'computop_maestro',
          'computop_bancontact',
          'arvato_creditcard',
          'arvato_ideal',
        ] as (string | undefined)[]
      ).includes(checkoutState.orderState.payment?.selected?.[0]?.paymentOptionKey)
    )
  ) {
    redirect(checkoutState.orderConfirmation?.redirect);
    window.scrollTo({ top: 0 });
    return <LoadingIndicator />;
  }

  if (checkoutState.notifications) {
    checkoutState.notifications.forEach(({ type, text, textTranslationKey }) =>
      setTimeout(
        () =>
          showNotification({
            level: type,
            text: text ?? formatMessage({ id: textTranslationKey }, getLinksForTranslation()),
          }),
        0
      )
    );
  }

  return (
    <CheckoutStateContext.Provider
      value={{
        checkoutState,
        ...mutations,
        setBasketEmpty,
        setPollingEnabled,
      }}
    >
      {children}
    </CheckoutStateContext.Provider>
  );
};

export const useCheckoutState = () => useContext(CheckoutStateContext);
